import { takeLatest, put, delay } from 'redux-saga/effects';
import { authorizationActions as actions } from '.';
import { IGetUserCompanyPermissionsPayload, IRegisterUser } from './types';
import authApi from '../authApi';
import { RouteConstants } from '../../../routes';
import { globalActions } from 'app/containers/Global/slice';
import { hiikoEnvActions } from 'app/containers/HiikoEnv/slice';
import { ErrorCodeEnum, localStorageEnum } from 'types';
import { PayloadAction } from '@reduxjs/toolkit';
import { notification } from 'antd';
import i18next from 'i18next';
import { resourceActions } from 'app/containers/Resource/slice';
import userApi from 'app/containers/User/userApi';
import {
  IAccessRightsNumbers,
  ISimpleUser,
} from 'app/containers/User/slice/types';
import { getPermissionNumbersFromBooleans } from 'app/containers/User/slice/helpers';
import { setUserDataVars } from '../../../../utils/fullstory/fullstory-actions';

function* initialLoadSaga() {
  try {
    const token = localStorage.getItem(localStorageEnum.token);
    const isLoginOrRegistrationPath = window.location.pathname.includes(
      '/auth/',
    );

    if (!token) {
      if (!isLoginOrRegistrationPath) {
        yield put(globalActions.setRedirectTo(RouteConstants.auth.login));
      }
      return;
    }
    const user: ISimpleUser = yield authApi.getUser();

    yield put(actions.setUser(user));

    setUserDataVars(`${user.name} ${user.lastName}`, user.email);

    yield put(hiikoEnvActions.initLoadEnvs());
    yield put(resourceActions.loadResourceTypes());
  } catch (err) {
    yield put(globalActions.setRedirectTo(RouteConstants.auth.login));
  } finally {
    yield put(actions.initialLoaded(true));
  }
}

function* registerUserSaga(action: PayloadAction<IRegisterUser>) {
  try {
    const data = action.payload;

    yield authApi.registerUser(data);

    yield put(globalActions.setRedirectTo(RouteConstants.auth.login));
    yield delay(500);

    notification.success({
      message: i18next?.t(
        'Auth.Notification.AccountCreated',
        'Account successfully created',
      ),
      placement: 'bottomRight',
    });
  } catch (err) {
    if (err?.data?.errorCode === ErrorCodeEnum.userAlreadyExists) {
      notification.error({
        message: i18next?.t(
          'Auth.Notification.UserWithThisEmailExists',
          'User with this email already exists.',
        ),
        placement: 'bottomRight',
      });
    } else {
      yield put(globalActions.handleError(err));
    }
  } finally {
    yield put(actions.setRegisterUserLoading(false));
  }
}

function* getUserEnvPermissionsSaga(action: PayloadAction<number>) {
  try {
    yield put(actions.setUserEnvPermissionsLoading(true));

    const envId = action.payload;

    const p = yield userApi.getUserHiikoEnvironmentPermissions(envId);

    const envPermissions: IAccessRightsNumbers = getPermissionNumbersFromBooleans(
      p,
    );

    yield put(actions.setUserEnvPermissions(envPermissions));
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setUserEnvPermissionsLoading(false));
  }
}

function* getUserCompanyPermissionsSaga(
  action: PayloadAction<IGetUserCompanyPermissionsPayload>,
) {
  try {
    yield put(actions.setUserCompanyPermissionsLoading(true));

    const { envId, companyId } = action.payload;

    const p = yield userApi.getUserCompanyPermissions({ envId, companyId });

    const companyPermissions: IAccessRightsNumbers = getPermissionNumbersFromBooleans(
      p,
    );

    yield put(actions.setUserCompanyPermissions(companyPermissions));
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setUserCompanyPermissionsLoading(false));
  }
}

function* resetPasswordSaga(action: PayloadAction<string>) {
  try {
    const email = action.payload;
    yield authApi.forgotPassword(email);

    yield put(globalActions.setRedirectTo(RouteConstants.auth.login));
    yield delay(500);
    notification.success({
      message: i18next?.t(
        'Auth.Notification.PasswordResetEmailSent',
        'Email sent',
      ),
      description: i18next?.t(
        'Auth.Notification.PasswordResetEmailSentDescription',
        "We've sent you a password reset link at the email address you provided. If you don't see it, check the Spam category in your email application.",
      ),
      placement: 'bottomRight',
    });
  } catch (err) {
    yield put(globalActions.handleError(err));
  }
}

export function* authorizationSaga() {
  yield takeLatest(actions.resetPassword.type, resetPasswordSaga);
  yield takeLatest(
    actions.getUserEnvPermissions.type,
    getUserEnvPermissionsSaga,
  );
  yield takeLatest(
    actions.getUserCompanyPermissions.type,
    getUserCompanyPermissionsSaga,
  );
  yield takeLatest(actions.initialLoad.type, initialLoadSaga);

  yield takeLatest(actions.registerUser.type, registerUserSaga);
}
